<template>
  <IoOverlay
    v-if="currentComponent"
    ref="overlay"
    :bounds="modalBounds"
    :z-index="modalZIndex"
    :class="{
      [shapeClass]: hasShape,
    }"
    :style="overlayStyles"
    @close="close"
    @keydown.native="handleModalKeydown"
  >
    <BaseLoader
      ref="modal"
      :disabled="!enableLoader"
      :show="loading"
      :style="styles"
    >
      <BaseCard
        v-if="!customModal"
        :style="styles"
      >
        <template #header>
          <slot name="header">
            <h5
              class="card-title"
              data-eq-part="headline"
            >
              {{ title || modalTitle }}
            </h5>
            <div
              v-if="!persistent"
              class="form-actions"
            >
              <BaseButtonV2
                :disabled="!initialized"
                type="link"
                color="text"
                small
                aria-label="Schließe Modal"
                aria-description="Schließt das Modal"
                @click="close"
                @keydown.self.enter="close"
                @keydown.self.space="close"
              >
                <BaseIcon
                  icon="fa-light fa-times"
                  :style="{ fontSize: '20px' }"
                />
              </BaseButtonV2>
            </div>
          </slot>
        </template>
        <template
          v-if="!currentComponent"
          #content
        >
          <slot name="content" />
        </template>
        <template
          v-else
          #content
        >
          <component
            :is="currentComponent"
            v-if="hasComponent"
            v-bind="modalProps"
            :bindings="modalBindings"
            @close="close"
          />
        </template>
      </BaseCard>
      <component
        :is="currentComponent"
        v-else-if="hasComponent && customModal"
        v-bind="modalProps"
        :bindings="modalBindings"
        @close="close"
      />
    </BaseLoader>
  </IoOverlay>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import {
  computed,
  ref,
  watch,
  ComponentPublicInstance,
} from 'vue';

import { useBaseModalStore } from '@/stores/modal';
import { useAccessibilityStore } from '@/apps/base/stores';
import DomObserver from '@/classes/DomObserver';

import BaseLoader from '@/components/BaseLoader.vue';

import BaseModalBindingsInterface from '@/interfaces/BaseModalBindingsInterface';

const accessibilityStore = useAccessibilityStore();
const { accessibilityMenu } = storeToRefs(accessibilityStore);
const { handleAccessibilityMenu, closeAccessibilityMenu } = accessibilityStore;

const baseModalStore = useBaseModalStore();
const {
  component: modalComponent,
  props: modalProps,
  bindings: modalBindings,
  title: modalTitle,
  styles: modalStyles,
  bounds: modalBounds,
  zIndex: modalZIndex,
  refs: modalRefs,
  tooltipShape: modalTooltipShape,
  customModal,
  persistent,
  enableLoader,
  loading,
} = storeToRefs(baseModalStore);

const initialized = ref(false);

const currentComponent = computed(() => {
  const active = baseModalStore.getActive();
  if (active) {
    return active.component.value;
  }
  return modalComponent.value;
});

const hasComponent = computed(() => (typeof currentComponent.value === 'boolean' ? false : !!currentComponent.value));

const overlay = ref(null);
const modal = ref<ComponentPublicInstance>();
let modalObserver: DomObserver<'mutation'> | undefined;
let bodyObserver: DomObserver<'mutation'> | undefined;

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  minHeight: {
    type: String,
    default: '150px',
  },
  maxHeight: {
    type: String,
    default: '80vh',
  },
});

const emit = defineEmits(['close']);

const hasShape = computed(() => modalTooltipShape.value);
const shapeClass = computed(() => {
  if (!hasShape.value) {
    return '';
  }
  return `io-overlay--shape-${modalTooltipShape.value!.orientation}`;
});
const shapePosition = computed(() => (hasShape.value ? modalTooltipShape.value!.position : 'unset'));
const overlayStyles = computed(() => ({
  '--shape-position': `calc(${shapePosition.value} - 8px)`,
}));

const customPositioning = computed(() => {
  if (!modalStyles.value) {
    return false;
  }
  return Object.keys(modalStyles.value).length > 0;
});
const styles = computed(() => ({
  display: 'grid',
  minHeight: props.minHeight,
  maxHeight: props.maxHeight,
  position: customPositioning.value ? 'absolute' : 'relative',
  ...modalStyles.value,
}));

const updateAccessibilityElements = () => {
  if (!modal.value) return;
  const internal = Array.from(
    (modal.value.$el as HTMLElement).querySelectorAll(
      'a, button, input, select, textarea, [tabindex="0"]',
    ),
  ) as HTMLElement[];

  // Merge in focusable options from BaseSelect lists that may be appended/teleported to <body>
  const merged: HTMLElement[] = [];
  const processedIds = new Set<string>();
  internal.forEach((el) => {
    merged.push(el);
    const controls = el.getAttribute('aria-controls');
    if (!controls || processedIds.has(controls)) return;
    const listRoot = document.getElementById(controls);
    if (!listRoot) return;
    // Only include when listbox is visible/open
    const isListbox = listRoot.classList.contains('input-list');
    const isOpen = listRoot.classList.contains('input-list--open') && listRoot.getAttribute('aria-hidden') !== 'true';
    if (isListbox && isOpen) {
      const options = Array.from(listRoot.querySelectorAll('li[tabindex="0"]')) as HTMLElement[];
      if (options.length) merged.push(...options);
      processedIds.add(controls);
    }
  });

  // Check if we should update submenu or main menu
  const activeMenu = accessibilityMenu.value.submenu || accessibilityMenu.value;
  const previousLastActive = activeMenu.lastActive;

  if (accessibilityMenu.value.submenu) {
    // Update submenu elements
    accessibilityMenu.value.submenu.elements = merged;

    if (previousLastActive && !merged.includes(previousLastActive)) {
      const active = (document.activeElement || null) as HTMLElement | null;
      if (active && merged.includes(active)) {
        accessibilityMenu.value.submenu.lastActive = active;
      } else {
        [accessibilityMenu.value.submenu.lastActive] = merged;
      }
    } else if (!previousLastActive) {
      [accessibilityMenu.value.submenu.lastActive] = merged;
    }
  } else {
    // Update main menu elements
    accessibilityMenu.value.elements = merged;

    if (previousLastActive && !merged.includes(previousLastActive)) {
      const active = (document.activeElement || null) as HTMLElement | null;
      if (active && merged.includes(active)) {
        accessibilityMenu.value.lastActive = active;
      } else {
        [accessibilityMenu.value.lastActive] = merged;
      }
    } else if (!previousLastActive) {
      [accessibilityMenu.value.lastActive] = merged;
    }
  }
};
watch(() => currentComponent.value, (value) => {
  if (value) {
    modalRefs.value = {
      modal,
      overlay,
    };

    // IMPORTANT: Create submenu structure IMMEDIATELY to prevent race conditions
    // Check if there's an active menu - if so, we need to initialize submenu BEFORE any observers fire
    const hasActiveMenu = accessibilityMenu.value.elements.length > 0 && accessibilityMenu.value.toggle;

    if (hasActiveMenu) {
      // Capture the element that opened the modal (should be the currently focused menu item)
      const triggerElement = (document.activeElement || accessibilityMenu.value.lastActive) as HTMLElement;

      // Initialize submenu structure immediately to prevent parent menu overwrite
      // The toggle is the element that opened the modal, not the first element in the modal
      accessibilityMenu.value.submenu = {
        toggle: triggerElement,
        elements: [],
        lastActive: null,
      };
    }

    if (modalBindings.value && (modalBindings.value).onMounted) {
      try {
        const onMountedResult = (modalBindings.value).onMounted();
        if (onMountedResult && typeof (onMountedResult as Promise<void>).then === 'function') {
          (onMountedResult as Promise<void>)
            .then(() => {
              initialized.value = true;
            })
            .catch(() => {
              initialized.value = true;
            });
        } else {
          initialized.value = true;
        }
      } catch (error) {
        console.error('Error during modal onMounted handler:', error);
        initialized.value = true;
      }
    } else {
      initialized.value = true;
    }

    setTimeout(() => {
      updateAccessibilityElements();

      // Focus the appropriate element
      if (accessibilityMenu.value.submenu) {
        accessibilityMenu.value.submenu.lastActive?.focus();
      } else {
        accessibilityMenu.value.lastActive?.focus();
      }
    }, 100);

    // Observe DOM changes inside the modal to keep focusable elements in sync
    modalObserver?.disconnect();
    modalObserver = new DomObserver<'mutation'>(() => {
      updateAccessibilityElements();
    }, 'mutation');

    if (modal.value?.$el) {
      modalObserver.observe(modal.value.$el as Node, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled', 'hidden', 'aria-hidden', 'tabindex', 'aria-expanded'],
      });
    }
  } else {
    initialized.value = false;
    modalObserver?.disconnect();
    modalObserver = undefined;
    bodyObserver?.disconnect();
    bodyObserver = undefined;

    // Only close accessibility menu if there's a submenu to close
    // (Tab navigation already handles closing when going past boundaries)
    if (accessibilityMenu.value.submenu) {
      closeAccessibilityMenu();
    }

    if (modalBindings.value && (modalBindings.value).onUnmounted) {
      modalBindings.value.onUnmounted();
    }
  }
});

// Observe <body> because some dropdown lists are appended there
watch(() => currentComponent.value, (value) => {
  if (value) {
    bodyObserver?.disconnect();
    bodyObserver = new DomObserver<'mutation'>(() => {
      updateAccessibilityElements();
    }, 'mutation');
    bodyObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'tabindex', 'aria-hidden', 'aria-expanded'],
    });
  }
});

const close = () => {
  if (persistent.value) return;
  emit('close', false);
  baseModalStore.handleClose();
};

const handleModalKeydown = (e: KeyboardEvent) => {
  handleAccessibilityMenu(e, close, { trapFocus: true });
};

defineExpose({
  hasComponent,
  hasShape,
  shapeClass,
  shapePosition,
  overlayStyles,
  customPositioning,
  styles,
  closeModal: close,
});
</script>

<style lang="scss" scoped>
.io-overlay--shape {
  &-top {
    border-radius: 0 0 0 10px;
  }

  &-bottom::after {
    content: '';
    clip-path: polygon(100% 0, 0 0, 50% 75%);
    background-color: #fff;
    position: absolute;
    bottom: 9px;
    left: var(--shape-position);
    width: 16px;
    height: 16px;
  }
}

.base-loader-wrapper {
  width: 90%;
  min-width: 800px;
  max-width: 1470px;
  transition: margin-left 225ms ease-in-out;

  @media screen and (max-width: 860px ) {
    min-width: unset;
    width: 90%;
  }
}

:deep(.card) {
  .card-body {
    overflow-y: auto;
    overflow-x: visible;
  }

  .card-header.big {
    max-height: 68px;
  }

  .card-header {
    flex: 0;
  }
}
</style>
